import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiAuth } from "./useApiAuth";
import type { PoolMember } from "../types/pools";
import { apiUrl } from "../lib/api";

const parseErrorDetail = async (response: Response, fallback: string) => {
  try {
    const err = await response.json();
    return err.detail || fallback;
  } catch {
    return fallback;
  }
};

export function usePoolMembers(poolCode: string) {
  const { acquireToken } = useApiAuth();
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ["pool-members", poolCode], [poolCode]);

  const query = useQuery({
    queryKey,
    enabled: Boolean(poolCode),
    queryFn: async () => {
      const accessToken = await acquireToken();
      if (!accessToken) {
        throw new Error("Authentication failed.");
      }

      const response = await fetch(apiUrl(`/api/pools/${poolCode}/members`), {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!response.ok) {
        throw new Error(await parseErrorDetail(response, "Failed to fetch members."));
      }

      const data = await response.json();
      return (data.members || []) as PoolMember[];
    },
  });

  const refetchMembers = async () => {
    await queryClient.invalidateQueries({ queryKey });
    await query.refetch();
  };

  const approveMutation = useMutation({
    mutationFn: async (memberEmail: string) => {
      const accessToken = await acquireToken();
      if (!accessToken) {
        throw new Error("Authentication failed.");
      }
      const response = await fetch(
        apiUrl(`/api/pools/${poolCode}/members/${encodeURIComponent(memberEmail)}/approve`),
        {
          method: "POST",
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      );
      if (!response.ok) {
        throw new Error(await parseErrorDetail(response, "Failed to approve member."));
      }
    },
    onSuccess: refetchMembers,
  });

  const rejectMutation = useMutation({
    mutationFn: async (memberEmail: string) => {
      const accessToken = await acquireToken();
      if (!accessToken) {
        throw new Error("Authentication failed.");
      }
      const response = await fetch(
        apiUrl(`/api/pools/${poolCode}/members/${encodeURIComponent(memberEmail)}/reject`),
        {
          method: "POST",
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      );
      if (!response.ok) {
        throw new Error(await parseErrorDetail(response, "Failed to reject member."));
      }
    },
    onSuccess: refetchMembers,
  });

  const disableMutation = useMutation({
    mutationFn: async (memberEmail: string) => {
      const accessToken = await acquireToken();
      if (!accessToken) {
        throw new Error("Authentication failed.");
      }
      const response = await fetch(
        apiUrl(`/api/pools/${poolCode}/members/${encodeURIComponent(memberEmail)}`),
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      );
      if (!response.ok) {
        throw new Error(await parseErrorDetail(response, "Failed to disable member."));
      }
    },
    onSuccess: refetchMembers,
  });

  const enableMutation = useMutation({
    mutationFn: async (memberEmail: string) => {
      const accessToken = await acquireToken();
      if (!accessToken) {
        throw new Error("Authentication failed.");
      }
      const response = await fetch(
        apiUrl(`/api/pools/${poolCode}/members/${encodeURIComponent(memberEmail)}/enable`),
        {
          method: "POST",
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      );
      if (!response.ok) {
        throw new Error(await parseErrorDetail(response, "Failed to enable member."));
      }
    },
    onSuccess: refetchMembers,
  });

  const firstMutationError =
    approveMutation.error ??
    rejectMutation.error ??
    disableMutation.error ??
    enableMutation.error;

  return {
    members: query.data ?? [],
    isLoading: query.isLoading,
    error:
      (query.error instanceof Error ? query.error.message : null) ??
      (firstMutationError instanceof Error ? firstMutationError.message : null),
    reload: async () => {
      await query.refetch();
    },
    approveMember: async (memberEmail: string) => {
      await approveMutation.mutateAsync(memberEmail);
    },
    rejectMember: async (memberEmail: string) => {
      await rejectMutation.mutateAsync(memberEmail);
    },
    disableMember: async (memberEmail: string) => {
      await disableMutation.mutateAsync(memberEmail);
    },
    enableMember: async (memberEmail: string) => {
      await enableMutation.mutateAsync(memberEmail);
    },
  };
}
